import React, { useState } from 'react';
import { useCart } from '../context/CartContext';
import { merchConfig } from '../data/mock';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import OrderConfirmationPage from './OrderConfirmationPage';

const CheckoutPage = ({ onBack }) => {
  const { cart, removeFromCart, updateQuantity, clearCart, getTotal } = useCart();
  const [shippingMethod, setShippingMethod] = useState('ship');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderResponse, setOrderResponse] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    instagram: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA',
    notes: ''
  });

  const subtotal = getTotal();
  const serviceFee = merchConfig.serviceFee;
  const shippingCost = shippingMethod === 'ship' ? merchConfig.shippingCost : 0;
  const total = subtotal + serviceFee + shippingCost;

  const handleInputChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckout = async () => {
    if (!customerInfo.name) {
      toast.error('Please fill in your name');
      return;
    }

    if (!customerInfo.email && !customerInfo.phone) {
      toast.error('Please provide either email or phone number');
      return;
    }

    // Prepare order data (no shipping address needed - coordinate via email/note)
    const orderData = {
      customer: {
        name: customerInfo.name,
        email: customerInfo.email || null,
        phone: customerInfo.phone || null,
        instagram: customerInfo.instagram || null
      },
      items: cart.map(item => ({
        name: item.name,
        size: item.size,
        quantity: item.quantity,
        price: item.price
      })),
      shipping: {
        method: shippingMethod
      },
      subtotal: subtotal,
      service_fee: serviceFee,
      shipping_cost: shippingCost,
      total: total,
      notes: customerInfo.notes || null
    };

    try {
      // Send order to backend
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/orders/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      const result = await response.json();

      if (result.success) {
        // Clear cart and show confirmation page
        clearCart();
        setOrderResponse(result);
        setShowConfirmation(true);
      } else {
        toast.error('Order submission failed. Please try again.');
      }
    } catch (error) {
      toast.error('Error submitting order. Please try again.');
      console.error('Order submission error:', error);
    }
  };

  // Show confirmation page after order submitted
  if (showConfirmation && orderResponse) {
    return <OrderConfirmationPage orderData={orderResponse} onBackToHome={onBack} />;
  }

  if (cart.length === 0) {
    return (
      <div className="checkout-container">
        <div className="checkout-content">
          <button onClick={onBack} className="back-button">
            <ArrowLeft size={20} />
            Back to Shop
          </button>
          <div className="empty-cart">
            <h2>Your cart is empty</h2>
            <p>Add some brutal merch to your cart!</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-content">
        <button onClick={onBack} className="back-button">
          <ArrowLeft size={20} />
          Continue Shopping
        </button>

        <h1 className="checkout-title">CHECKOUT</h1>

        <div className="checkout-grid">
          {/* Cart Items */}
          <div className="cart-section">
            <h2 className="section-heading">Your Order</h2>
            {cart.map((item) => (
              <div key={item.size} className="cart-item">
                <img src={item.image} alt={item.name} className="cart-item-image" />
                <div className="cart-item-details">
                  <h3>{item.name}</h3>
                  <p>Size: {item.size}</p>
                  <p className="item-price">${item.price.toFixed(2)} each</p>
                </div>
                <div className="cart-item-actions">
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={item.quantity}
                    onChange={(e) => updateQuantity(item.size, parseInt(e.target.value) || 1)}
                    className="quantity-input"
                  />
                  <button
                    onClick={() => removeFromCart(item.size)}
                    className="remove-button"
                    aria-label="Remove item"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}

            <div className="cart-summary">
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span>Service Fee:</span>
                <span>${serviceFee.toFixed(2)}</span>
              </div>
              <div className="summary-row" style={{
                fontWeight: shippingMethod === 'ship' ? 'bold' : 'normal',
                color: shippingMethod === 'ship' ? '#8B0000' : '#fff'
              }}>
                <span>Shipping:</span>
                <span>{shippingMethod === 'pickup' ? 'FREE (Pickup)' : `$${shippingCost.toFixed(2)}`}</span>
              </div>
              <div className="summary-row total">
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Checkout Form */}
          <div className="checkout-form-section">
            <h2 className="section-heading">Customer Information</h2>
            
            <div className="form-group">
              <label>Name *</label>
              <input
                type="text"
                name="name"
                value={customerInfo.name}
                onChange={handleInputChange}
                className="checkout-input"
                required
              />
            </div>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={customerInfo.email}
                onChange={handleInputChange}
                className="checkout-input"
              />
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={customerInfo.phone}
                onChange={handleInputChange}
                className="checkout-input"
              />
            </div>

            <div className="form-group">
              <label>Instagram Handle (Optional)</label>
              <input
                type="text"
                name="instagram"
                value={customerInfo.instagram}
                onChange={handleInputChange}
                className="checkout-input"
                placeholder="@yourhandle"
              />
            </div>

            <h2 className="section-heading" style={{ marginTop: '30px' }}>Delivery Method</h2>
            
            <div className="shipping-options">
              <label className="shipping-option">
                <input
                  type="radio"
                  value="pickup"
                  checked={shippingMethod === 'pickup'}
                  onChange={(e) => setShippingMethod(e.target.value)}
                />
                <div>
                  <strong>Free Pickup</strong>
                  <p>
                    Woodstock, GA 30066 - <strong>Email <a href="mailto:gored.offc@gmail.com">gored.offc@gmail.com</a></strong> or <a href="https://www.instagram.com/gored.band?igsh=MXQ1dXhxdzhpcmk0dg==" target="_blank" rel="noopener noreferrer">message Instagram</a> to arrange pickup
                  </p>
                </div>
              </label>

              <label className="shipping-option">
                <input
                  type="radio"
                  value="ship"
                  checked={shippingMethod === 'ship'}
                  onChange={(e) => setShippingMethod(e.target.value)}
                />
                <div>
                  <strong>Shipping Available</strong>
                  <p>Include your shipping address in the payment note or email us at <strong><a href="mailto:gored.offc@gmail.com">gored.offc@gmail.com</a></strong> - we'll coordinate shipping cost & details with you directly</p>
                </div>
              </label>
            </div>

            <div className="form-group" style={{ marginTop: '30px' }}>
              <label>Notes (Optional)</label>
              <textarea
                name="notes"
                value={customerInfo.notes}
                onChange={handleInputChange}
                className="checkout-input"
                rows="4"
                placeholder={shippingMethod === 'ship' ? "Include your full shipping address here, or email us at gored.offc@gmail.com..." : "Any special requests or messages..."}
              />
              {shippingMethod === 'ship' && (
                <p style={{ fontSize: '14px', color: '#8B0000', marginTop: '8px' }}>
                  💡 Tip: Include your shipping address here or in the Zelle/CashApp payment note
                </p>
              )}
            </div>

            <div className="payment-info" style={{
              background: '#1a0000',
              border: '2px solid #8B0000',
              padding: '20px',
              marginTop: '30px',
              marginBottom: '20px'
            }}>
              <h2 className="section-heading" style={{ marginBottom: '15px' }}>Payment Instructions</h2>
              <p style={{ fontSize: '16px', marginBottom: '15px', lineHeight: '1.6' }}>
                Send <strong style={{ color: '#8B0000' }}>${total.toFixed(2)}</strong> to:
              </p>
              <div style={{ marginBottom: '15px' }}>
                <p style={{ fontSize: '18px', marginBottom: '8px' }}>
                  <strong>Zelle:</strong> <span style={{ color: '#8B0000', fontSize: '20px' }}>770-296-7715</span>
                </p>
                <p style={{ fontSize: '18px' }}>
                  <strong>CashApp:</strong> <span style={{ color: '#8B0000', fontSize: '20px' }}>$taniskn</span>
                </p>
              </div>

              {/* BOLD ORDER NUMBER WARNING */}
              <div style={{
                background: '#0a0a0a',
                border: '3px solid #8B0000',
                padding: '20px',
                marginTop: '20px'
              }}>
                <p style={{ 
                  fontSize: '18px', 
                  fontWeight: 'bold', 
                  color: '#ff6b6b', 
                  textAlign: 'center',
                  marginBottom: '10px'
                }}>
                  🚨 YOU MUST INCLUDE YOUR ORDER NUMBER IN THE PAYMENT NOTE 🚨
                </p>
                <p style={{ fontSize: '14px', color: '#fff', textAlign: 'center', lineHeight: '1.6' }}>
                  After clicking "Submit Order" below, you'll receive an order number. Include that order number in your Zelle/CashApp payment note along with your contact info.
                </p>
                <p style={{ 
                  fontSize: '14px', 
                  fontWeight: 'bold', 
                  color: '#ff6b6b', 
                  textAlign: 'center',
                  marginTop: '10px'
                }}>
                  ⚠️ WITHOUT YOUR ORDER NUMBER WE CANNOT PROCESS YOUR ORDER ⚠️
                </p>
              </div>
            </div>

            <button onClick={handleCheckout} className="btn-checkout" style={{
              background: '#8B0000',
              color: '#fff',
              border: 'none',
              padding: '15px 40px',
              fontSize: '18px',
              fontWeight: 'bold',
              cursor: 'pointer',
              width: '100%',
              transition: 'all 0.3s'
            }}>
              SUBMIT ORDER
            </button>

            <p style={{ 
              fontSize: '14px', 
              color: '#999', 
              textAlign: 'center', 
              marginTop: '15px'
            }}>
              Your cart will be saved even if you close the browser
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;